import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import "./FindVerse.css";
import {
  getEnglishBookName,
  getTamilBookName,
  getDeutschBookName,
} from "../BookNames";
import { AuthContext } from "../../Authentication/AuthContext";
import {
  getBookChapterCount,
  getChapterVerses,
  getStaticBibleSections,
  prefetchWholeBookInBackground,
  startBibleDownloadCache,
} from "../DownloadCache";

const TESTAMENT_SELECT_VALUE = "";
const NEIGHBOR_PREFETCH_DELAY_MS = 120;

const normalizeLanguageKey = (appLanguage) => {
  const normalizedLanguage = String(appLanguage || "").trim().toLowerCase();

  if (
    normalizedLanguage === "tamil" ||
    normalizedLanguage === "தமிழ்" ||
    appLanguage === "Tamil" ||
    appLanguage === "தமிழ்"
  ) {
    return "tamil";
  }

  if (normalizedLanguage === "deutsch" || normalizedLanguage === "german") {
    return "deutsch";
  }

  return "english";
};

const normalizeIncomingLanguageKey = (languageValue) => {
  const normalizedValue = String(languageValue || "")
    .trim()
    .toLowerCase();

  if (!normalizedValue) return "";

  if (normalizedValue === "tamil" || normalizedValue === "தமிழ்") {
    return "tamil";
  }

  if (normalizedValue === "english") {
    return "english";
  }

  if (normalizedValue === "deutsch" || normalizedValue === "german") {
    return "deutsch";
  }

  return "";
};

const getSelectedLanguagesFromHome = ({
  statePrefill,
  params,
  fallbackLanguage,
}) => {
  const stateLanguages = Array.isArray(statePrefill.selectedLanguages)
    ? statePrefill.selectedLanguages
    : [];

  const queryLanguages = String(params.get("languages") || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  const incomingLanguages =
    stateLanguages.length > 0 ? stateLanguages : queryLanguages;

  const selectedHomeLanguages = incomingLanguages.reduce(
    (uniqueLanguages, item) => {
      const normalizedLanguage = normalizeIncomingLanguageKey(item);

      if (normalizedLanguage && !uniqueLanguages.includes(normalizedLanguage)) {
        uniqueLanguages.push(normalizedLanguage);
      }

      return uniqueLanguages;
    },
    [],
  );

  if (selectedHomeLanguages.length > 0) {
    return selectedHomeLanguages;
  }

  return [normalizeLanguageKey(fallbackLanguage)];
};

const getBookCacheKey = (bookValue) =>
  String(bookValue || "")
    .trim()
    .toLowerCase();

const getCompactBookKey = (bookValue) =>
  String(bookValue || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const resolveBookKeyFromBooks = (books, bookValue, fallbackValue = "") => {
  if (!bookValue) return fallbackValue;

  const incomingCompactKey = getCompactBookKey(bookValue);
  const matchedBook = books.find(
    (book) => getCompactBookKey(book.book_name) === incomingCompactKey,
  );

  if (matchedBook) return getBookCacheKey(matchedBook.book_name);

  return getBookCacheKey(bookValue);
};

const getOriginalBookNameFromBooks = (books, bookValue) => {
  if (!bookValue) return "";

  const incomingCompactKey = getCompactBookKey(bookValue);
  const matchedBook = books.find(
    (book) => getCompactBookKey(book.book_name) === incomingCompactKey,
  );

  return matchedBook?.book_name || bookValue;
};

const getFullChapterVerseRange = (preparedVerses) => {
  if (!Array.isArray(preparedVerses) || preparedVerses.length === 0) {
    return "";
  }

  const verseNumbers = preparedVerses
    .map((verse) => Number(verse?.verse_number))
    .filter((verseNumber) => Number.isFinite(verseNumber) && verseNumber > 0);

  if (!verseNumbers.length) {
    return "";
  }

  const lastVerseNumber = Math.max(...verseNumbers);

  return `1-${lastVerseNumber}`;
};

const getLanguageText = (lang) => {
  if (lang === "tamil") return "Tamil";
  if (lang === "deutsch") return "Deutsch";
  return "English";
};

const getBookDisplayName = (bookName, appLanguage) => {
  if (appLanguage === "English") return getEnglishBookName(bookName);
  if (appLanguage === "Deutsch") return getDeutschBookName(bookName);
  return getTamilBookName(bookName);
};

const getBookNameForResultLanguage = (bookName, lang) => {
  if (lang === "tamil") return getTamilBookName(bookName);
  if (lang === "deutsch") return getDeutschBookName(bookName);
  return getEnglishBookName(bookName);
};

const getTestamentDisplayName = (testamentName, appLanguage) => {
  if (appLanguage === "English") return testamentName;

  if (appLanguage === "Deutsch") {
    return testamentName === "Old Testament"
      ? "Altes Testament"
      : "Neues Testament";
  }

  return testamentName === "Old Testament" ? "பழைய ஏற்பாடு" : "புதிய ஏற்பாடு";
};

const getTestamentTypeFromName = (testamentName) =>
  testamentName === "Old Testament" ? "old" : "new";

const getTestamentSectionFromType = (testaments, testamentType) => {
  if (!testamentType) return null;

  return testaments.find(
    (testament) =>
      getTestamentTypeFromName(testament.testament) === testamentType,
  );
};

const getLabels = (appLanguage) => {
  if (appLanguage === "English") {
    return {
      testament: "Testament",
      book: "Book",
      chapter: "Chapter",
      verse: "Verse",
      language: "Language",
      select: "Select",
      versePlaceholder: "Example: 1 or 1-14",
      submit: "Submit",
      reset: "Reset",
      noResults: "No verses found.",
      selectTestamentAlert: "Please select the testament.",
      selectBookAlert: "Please select the book.",
      selectChapterAlert: "Please select the chapter.",
      enterVerseAlert: "Please enter the verse number.",
      selectLanguageAlert: "Please select at least one language.",
      invalidVerseAlert: "Please enter a valid verse number or verse range.",
      alertTitle: "Required",
      previous: "‹ Previous",
      next: "Next ›",
    };
  }

  if (appLanguage === "Deutsch") {
    return {
      testament: "Testament",
      book: "Buch",
      chapter: "Kapitel",
      verse: "Vers",
      language: "Sprache",
      select: "Select",
      versePlaceholder: "Example: 1 or 1-14",
      submit: "Submit",
      reset: "Reset",
      noResults: "Keine Verse gefunden.",
      selectTestamentAlert: "Bitte Testament auswählen.",
      selectBookAlert: "Bitte Buch auswählen.",
      selectChapterAlert: "Bitte Kapitel auswählen.",
      enterVerseAlert: "Bitte Versnummer eingeben.",
      selectLanguageAlert: "Bitte mindestens eine Sprache auswählen.",
      invalidVerseAlert:
        "Bitte eine gültige Versnummer oder einen gültigen Versbereich eingeben.",
      alertTitle: "Required",
      previous: "‹ Vorheriges",
      next: "Nächstes ›",
    };
  }

  return {
    testament: "ஏற்பாடு",
    book: "ஆகமம்",
    chapter: "அதிகாரம்",
    verse: "வசனம்",
    language: "மொழி",
    select: "Select",
    versePlaceholder: "Example: 1 or 1-14",
    submit: "Submit",
    reset: "Reset",
    noResults: "வசனங்கள் கிடைக்கவில்லை.",
    selectTestamentAlert: "ஏற்பாட்டைத் தேர்ந்தெடுக்கவும்.",
    selectBookAlert: "ஆகமத்தைத் தேர்ந்தெடுக்கவும்.",
    selectChapterAlert: "அதிகாரத்தைத் தேர்ந்தெடுக்கவும்.",
    enterVerseAlert: "வசன இலக்கத்தை உள்ளிடவும்.",
    selectLanguageAlert: "குறைந்தது ஒரு மொழியைத் தேர்ந்தெடுக்கவும்.",
    invalidVerseAlert: "சரியான வசன இலக்கம் அல்லது வசன வரம்பை உள்ளிடவும்.",
    alertTitle: "தேவை",
    previous: "‹ முந்தையது",
    next: "அடுத்தது ›",
  };
};

const parseVerseInput = (value) => {
  const cleanedValue = String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[–—]/g, "-");

  if (!cleanedValue) return null;

  if (cleanedValue.includes("-")) {
    const [startRaw, endRaw] = cleanedValue.split("-");
    const start = Number.parseInt(startRaw, 10);
    const end = Number.parseInt(endRaw, 10);

    if (
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      start <= 0 ||
      end <= 0 ||
      start > end
    ) {
      return null;
    }

    return {
      start,
      end,
    };
  }

  const verseNumberValue = Number.parseInt(cleanedValue, 10);

  if (!Number.isFinite(verseNumberValue) || verseNumberValue <= 0) return null;

  return {
    start: verseNumberValue,
    end: verseNumberValue,
  };
};

const CustomSelect = ({
  id,
  value,
  options,
  placeholder,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  const selectedOption = options.find((option) => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!selectRef.current || selectRef.current.contains(event.target)) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);

  const handleOptionClick = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div className="bibleversefind-custom-select" ref={selectRef}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        className={`bibleversefind-custom-select-button ${
          value ? "has-selected-value" : ""
        }`}
        onClick={() => setIsOpen((previousValue) => !previousValue)}
      >
        <span className="bibleversefind-custom-select-text">{displayText}</span>

        {!value ? (
          <span
            className="bibleversefind-custom-select-arrow"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              className="bibleversefind-custom-select-arrow-icon"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        ) : null}
      </button>

      {isOpen && !disabled ? (
        <div className="bibleversefind-custom-select-options">
          {options.map((option) => (
            <button
              key={`${id}-${option.value}`}
              type="button"
              className={`bibleversefind-custom-select-option ${
                option.value === value ? "active" : ""
              }`}
              onClick={() => handleOptionClick(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

function FindVerse() {
  const { language } = useContext(AuthContext);
  const location = useLocation();

  const pageScrollRef = useRef(null);
  const resultContainerRef = useRef(null);
  const chapterHeaderRef = useRef(null);
  const verseInputRef = useRef(null);
  const homePrefillAppliedRef = useRef(false);
  const activeRequestRef = useRef(0);
  const activeNativeInputRef = useRef(null);
  const inputFocusScrollTopRef = useRef(0);
  const pendingResultScrollRef = useRef(false);

  const [testaments, setTestaments] = useState([]);
  const [selectedTestament, setSelectedTestament] = useState(
    TESTAMENT_SELECT_VALUE,
  );
  const [selectedBook, setSelectedBook] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [verseNumber, setVerseNumber] = useState("");
  const [selectedLanguages, setSelectedLanguages] = useState([
    normalizeLanguageKey(language),
  ]);
  const [verses, setVerses] = useState([]);
  const [error, setError] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);
  const [isNativeInputFocused, setIsNativeInputFocused] = useState(false);
  const [loadedContext, setLoadedContext] = useState(null);

  const labels = useMemo(() => getLabels(language), [language]);
  const availableLanguages = useMemo(() => ["tamil", "english", "deutsch"], []);

  const rememberFindVerseInputScrollPosition = useCallback(() => {
    if (pageScrollRef.current) {
      inputFocusScrollTopRef.current = pageScrollRef.current.scrollTop;
    }
  }, []);

  const scrollFocusedFindVerseInputIntoSafeArea = useCallback((inputElement) => {
    const scroller = pageScrollRef.current;
    const input = inputElement || activeNativeInputRef.current;

    if (!scroller || !input) return;

    const visualViewport = window.visualViewport;
    const visualViewportTop = visualViewport ? visualViewport.offsetTop : 0;
    const visualViewportHeight = visualViewport
      ? visualViewport.height
      : window.innerHeight;

    const inputRect = input.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();

    const safeTop = Math.max(scrollerRect.top + 14, visualViewportTop + 14);
    const safeBottom = Math.min(
      scrollerRect.bottom - 18,
      visualViewportTop + visualViewportHeight - 26,
    );

    let nextScrollTop = scroller.scrollTop;

    if (inputRect.bottom > safeBottom) {
      nextScrollTop += inputRect.bottom - safeBottom;
    } else if (inputRect.top < safeTop) {
      nextScrollTop -= safeTop - inputRect.top;
    }

    const maxScrollTop = Math.max(
      0,
      scroller.scrollHeight - scroller.clientHeight,
    );
    const finalScrollTop = Math.max(0, Math.min(nextScrollTop, maxScrollTop));

    scroller.scrollTo({
      top: finalScrollTop,
      behavior: "smooth",
    });
  }, []);

  const handleNativeInputFocus = useCallback(
    (event) => {
      const input = event.currentTarget;

      activeNativeInputRef.current = input;
      setIsNativeInputFocused(true);

      window.setTimeout(() => {
        scrollFocusedFindVerseInputIntoSafeArea(input);
      }, 120);

      window.setTimeout(() => {
        scrollFocusedFindVerseInputIntoSafeArea(input);
      }, 320);

      window.setTimeout(() => {
        scrollFocusedFindVerseInputIntoSafeArea(input);
      }, 620);
    },
    [scrollFocusedFindVerseInputIntoSafeArea],
  );

  const handleNativeInputBlur = useCallback(() => {
    window.setTimeout(() => {
      const activeElement = document.activeElement;

      if (
        activeElement &&
        activeElement.closest &&
        activeElement.closest(".bibleversefind-container input[type='text']")
      ) {
        return;
      }

      activeNativeInputRef.current = null;
      setIsNativeInputFocused(false);
    }, 180);
  }, []);

  const homePrefill = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const statePrefill = location.state?.prefill || {};
    const hasHomePrefill =
      params.get("home") === "1" || location.state?.fromHome === true;

    if (!hasHomePrefill) return null;

    return {
      testamentType:
        statePrefill.testamentType || params.get("testament") || "",
      book: statePrefill.book || params.get("book") || "",
      chapter: statePrefill.chapter || params.get("chapter") || "",
      verse: statePrefill.verse || params.get("verse") || "",
      selectedLanguages: getSelectedLanguagesFromHome({
        statePrefill,
        params,
        fallbackLanguage: language,
      }),
    };
  }, [language, location.search, location.state]);

  const selectedTestamentObject = useMemo(() => {
    if (!selectedTestament) return null;

    return testaments.find(
      (testament) => String(testament.id) === String(selectedTestament),
    );
  }, [selectedTestament, testaments]);

  const selectedTestamentType = useMemo(() => {
    if (!selectedTestamentObject) return "";
    return getTestamentTypeFromName(selectedTestamentObject.testament);
  }, [selectedTestamentObject]);

  const books = useMemo(() => {
    return selectedTestamentObject?.books || [];
  }, [selectedTestamentObject]);

  const selectedOriginalBookName = useMemo(() => {
    return getOriginalBookNameFromBooks(books, selectedBook);
  }, [books, selectedBook]);

  const chapterCount = useMemo(() => {
    if (!selectedTestamentType || !selectedBook) return 0;
    return getBookChapterCount(selectedTestamentType, selectedBook);
  }, [selectedBook, selectedTestamentType]);

  const orderedLanguages = useMemo(() => {
    const selectedSet = new Set(selectedLanguages);

    return [
      ...selectedLanguages,
      ...availableLanguages.filter((lang) => !selectedSet.has(lang)),
    ];
  }, [availableLanguages, selectedLanguages]);

  const testamentOptions = useMemo(() => {
    return [
      {
        value: TESTAMENT_SELECT_VALUE,
        label: labels.select,
      },
      ...testaments.map((testament) => ({
        value: String(testament.id),
        label: getTestamentDisplayName(testament.testament, language),
      })),
    ];
  }, [labels.select, language, testaments]);

  const bookOptions = useMemo(() => {
    return [
      {
        value: "",
        label: labels.select,
      },
      ...books.map((book) => ({
        value: getBookCacheKey(book.book_name),
        label: getBookDisplayName(book.book_name, language),
      })),
    ];
  }, [books, labels.select, language]);

  const chapterOptions = useMemo(() => {
    const options = [
      {
        value: "",
        label: labels.select,
      },
    ];

    for (
      let chapterNumberValue = 1;
      chapterNumberValue <= chapterCount;
      chapterNumberValue += 1
    ) {
      options.push({
        value: String(chapterNumberValue),
        label: String(chapterNumberValue),
      });
    }

    return options;
  }, [chapterCount, labels.select]);

  const showAlert = (message) => {
    setAlertMessage(message);
  };

  const alignResultToTop = useCallback((behavior = "auto") => {
    const pageScroller = pageScrollRef.current;
    const resultContainer = resultContainerRef.current;

    if (resultContainer) {
      resultContainer.scrollTop = 0;
    }

    if (pageScroller && resultContainer) {
      pageScroller.scrollTo({
        top: resultContainer.offsetTop,
        behavior,
      });

      return;
    }

    resultContainer?.scrollIntoView({
      behavior,
      block: "start",
    });
  }, []);

  const requestResultTopAlign = useCallback(() => {
    pendingResultScrollRef.current = true;
  }, []);

  useLayoutEffect(() => {
    if (!pendingResultScrollRef.current || !isSubmitted) {
      return undefined;
    }

    if (!verses.length && !error) {
      return undefined;
    }

    let frameOne = 0;
    let frameTwo = 0;
    let timeoutOne = 0;
    let timeoutTwo = 0;

    const alignNow = () => {
      alignResultToTop("auto");
    };

    alignNow();

    frameOne = window.requestAnimationFrame(() => {
      alignNow();

      frameTwo = window.requestAnimationFrame(() => {
        alignNow();
      });
    });

    timeoutOne = window.setTimeout(() => {
      alignNow();
    }, 80);

    timeoutTwo = window.setTimeout(() => {
      alignNow();
      pendingResultScrollRef.current = false;
    }, 180);

    return () => {
      window.cancelAnimationFrame(frameOne);
      window.cancelAnimationFrame(frameTwo);
      window.clearTimeout(timeoutOne);
      window.clearTimeout(timeoutTwo);
    };
  }, [
    alignResultToTop,
    error,
    isSubmitted,
    verses.length,
    loadedContext?.chapterNumber,
  ]);

  useEffect(() => {
    startBibleDownloadCache();
    setTestaments(getStaticBibleSections());
  }, []);

  useEffect(() => {
    const updateMobileViewportVariables = () => {
      const visualViewport = window.visualViewport;

      const visualViewportHeight =
        visualViewport && visualViewport.height
          ? visualViewport.height
          : window.innerHeight;

      const isInputFocused = Boolean(activeNativeInputRef.current);

      const keyboardSpace =
        isInputFocused && visualViewport && visualViewport.height
          ? Math.max(
              0,
              window.innerHeight -
                visualViewport.height -
                visualViewport.offsetTop,
            )
          : 0;

      const bottomBrowserSpace =
        !isInputFocused && visualViewport && visualViewport.height
          ? Math.max(
              0,
              window.innerHeight -
                visualViewport.height -
                visualViewport.offsetTop,
            )
          : 0;

      const safeLayoutHeight = isInputFocused
        ? window.innerHeight
        : visualViewportHeight;

      document.documentElement.style.setProperty(
        "--bibleversefind-visual-viewport-height",
        `${Math.round(safeLayoutHeight)}px`,
      );

      document.documentElement.style.setProperty(
        "--bibleversefind-mobile-bottom-space",
        `${Math.round(bottomBrowserSpace)}px`,
      );

      document.documentElement.style.setProperty(
        "--bibleversefind-keyboard-space",
        `${Math.round(keyboardSpace)}px`,
      );

      if (isInputFocused) {
        window.setTimeout(() => {
          scrollFocusedFindVerseInputIntoSafeArea(activeNativeInputRef.current);
        }, 40);
      }
    };

    updateMobileViewportVariables();

    window.addEventListener("resize", updateMobileViewportVariables);
    window.addEventListener(
      "orientationchange",
      updateMobileViewportVariables,
    );

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        "resize",
        updateMobileViewportVariables,
      );
      window.visualViewport.addEventListener(
        "scroll",
        updateMobileViewportVariables,
      );
    }

    return () => {
      window.removeEventListener("resize", updateMobileViewportVariables);
      window.removeEventListener(
        "orientationchange",
        updateMobileViewportVariables,
      );

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          updateMobileViewportVariables,
        );
        window.visualViewport.removeEventListener(
          "scroll",
          updateMobileViewportVariables,
        );
      }

      document.documentElement.style.removeProperty(
        "--bibleversefind-visual-viewport-height",
      );
      document.documentElement.style.removeProperty(
        "--bibleversefind-mobile-bottom-space",
      );
      document.documentElement.style.removeProperty(
        "--bibleversefind-keyboard-space",
      );
    };
  }, [scrollFocusedFindVerseInputIntoSafeArea]);

  useEffect(() => {
    const stopHeaderTouchDrag = (event) => {
      if (!event.touches || event.touches.length !== 1) {
        return;
      }

      event.preventDefault();
    };

    const headerElements = [
      chapterHeaderRef.current,
      document.querySelector(".top-header-shell"),
      document.querySelector(".top-header"),
      document.querySelector(".contact-info"),
    ].filter(Boolean);

    headerElements.forEach((headerElement) => {
      headerElement.addEventListener("touchmove", stopHeaderTouchDrag, {
        passive: false,
      });
    });

    return () => {
      headerElements.forEach((headerElement) => {
        headerElement.removeEventListener("touchmove", stopHeaderTouchDrag);
      });
    };
  }, [verses.length]);

  useEffect(() => {
    if (!testaments.length || !homePrefill || homePrefillAppliedRef.current) {
      return;
    }

    const testamentSection = getTestamentSectionFromType(
      testaments,
      homePrefill.testamentType,
    );

    if (!testamentSection) return;

    const resolvedBook = resolveBookKeyFromBooks(
      testamentSection.books,
      homePrefill.book,
      "",
    );

    setSelectedTestament(String(testamentSection.id));
    setSelectedBook(resolvedBook);
    setSelectedChapter(homePrefill.chapter || "");
    setVerseNumber(homePrefill.verse || "");
    setSelectedLanguages(
      homePrefill.selectedLanguages || [normalizeLanguageKey(language)],
    );
    setVerses([]);
    setError("");
    setAlertMessage("");
    setIsSubmitted(false);

    homePrefillAppliedRef.current = true;

    if (
      homePrefill.testamentType &&
      resolvedBook &&
      homePrefill.chapter &&
      homePrefill.verse
    ) {
      setShouldAutoSubmit(true);
    }
  }, [homePrefill, language, testaments]);

  useEffect(() => {
    if (homePrefill) return;

    setSelectedLanguages([normalizeLanguageKey(language)]);
  }, [homePrefill, language]);

  useEffect(() => {
    if (!selectedBook || !selectedTestamentType) {
      return;
    }

    prefetchWholeBookInBackground(selectedTestamentType, selectedBook);
  }, [selectedBook, selectedTestamentType]);

  const warmChapter = useCallback(
    ({ testamentType, bookKey, chapterNumber }) => {
      if (!testamentType || !bookKey || !chapterNumber) return;

      window.setTimeout(() => {
        getChapterVerses(testamentType, bookKey, Number(chapterNumber));
      }, NEIGHBOR_PREFETCH_DELAY_MS);
    },
    [],
  );

  const loadChapter = useCallback(
    async ({
      testamentId,
      testamentType,
      bookKey,
      originalBookName,
      chapterNumber,
      verseInput,
      shouldScroll = false,
      shouldUpdateVerseInput = true,
    }) => {
      activeRequestRef.current += 1;

      const requestId = activeRequestRef.current;
      const safeVerseInput = verseInput || "";
      const parsedVerseInput = safeVerseInput
        ? parseVerseInput(safeVerseInput)
        : null;

      if (shouldScroll) {
        requestResultTopAlign();
      }

      setAlertMessage("");
      setError("");
      setVerses([]);
      setIsSubmitted(true);

      setLoadedContext({
        testamentId: String(testamentId),
        testamentType,
        bookKey,
        originalBookName,
        chapterNumber: Number(chapterNumber),
        verseInput: safeVerseInput,
      });

      try {
        const loadedVerses = await getChapterVerses(
          testamentType,
          bookKey,
          Number(chapterNumber),
        );

        if (activeRequestRef.current !== requestId) return;

        const safeVerses = Array.isArray(loadedVerses) ? loadedVerses : [];

        const preparedVerses = safeVerses.map((verse) => ({
          ...verse,
          testamentType,
          book_name: verse.book_name || originalBookName,
        }));

        const fullChapterRange = getFullChapterVerseRange(preparedVerses);

        const filteredVerses = parsedVerseInput
          ? preparedVerses.filter((verse) => {
              const verseValue = Number(verse.verse_number);

              return (
                verseValue >= parsedVerseInput.start &&
                verseValue <= parsedVerseInput.end
              );
            })
          : preparedVerses;

        const visibleVerseInput = parsedVerseInput
          ? safeVerseInput
          : fullChapterRange;

        setSelectedTestament(String(testamentId));
        setSelectedBook(bookKey);
        setSelectedChapter(String(chapterNumber));

        if (shouldUpdateVerseInput) {
          setVerseNumber(visibleVerseInput);
        }

        setLoadedContext({
          testamentId: String(testamentId),
          testamentType,
          bookKey,
          originalBookName,
          chapterNumber: Number(chapterNumber),
          verseInput: visibleVerseInput,
        });

        setVerses(filteredVerses);

        if (!filteredVerses.length) {
          setError(labels.noResults);
        }

        const totalChapters = getBookChapterCount(testamentType, bookKey);

        if (chapterNumber > 1) {
          warmChapter({
            testamentType,
            bookKey,
            chapterNumber: Number(chapterNumber) - 1,
          });
        }

        if (chapterNumber < totalChapters) {
          warmChapter({
            testamentType,
            bookKey,
            chapterNumber: Number(chapterNumber) + 1,
          });
        }
      } catch (loadError) {
        if (activeRequestRef.current !== requestId) return;

        setVerses([]);
        setError(labels.noResults);
      }
    },
    [labels.noResults, requestResultTopAlign, warmChapter],
  );

  useEffect(() => {
    if (!shouldAutoSubmit) return;

    if (
      !selectedTestament ||
      !selectedBook ||
      !selectedChapter ||
      !verseNumber
    ) {
      return;
    }

    const parsedVerseInput = parseVerseInput(verseNumber);

    if (!parsedVerseInput) return;

    setShouldAutoSubmit(false);

    loadChapter({
      testamentId: selectedTestament,
      testamentType: selectedTestamentType,
      bookKey: selectedBook,
      originalBookName: selectedOriginalBookName || selectedBook,
      chapterNumber: Number(selectedChapter),
      verseInput: verseNumber,
      shouldScroll: false,
    });
  }, [
    loadChapter,
    selectedBook,
    selectedChapter,
    selectedOriginalBookName,
    selectedTestament,
    selectedTestamentType,
    shouldAutoSubmit,
    verseNumber,
  ]);

  const resetFormFields = useCallback(() => {
    activeRequestRef.current += 1;

    setSelectedTestament(TESTAMENT_SELECT_VALUE);
    setSelectedBook("");
    setSelectedChapter("");
    setVerseNumber("");
    setSelectedLanguages([normalizeLanguageKey(language)]);
    setVerses([]);
    setError("");
    setAlertMessage("");
    setIsSubmitted(false);
    setShouldAutoSubmit(false);
    setLoadedContext(null);
  }, [language]);

  const handleTestamentChange = (value) => {
    activeRequestRef.current += 1;

    setSelectedTestament(value);
    setSelectedBook("");
    setSelectedChapter("");
    setVerseNumber("");
    setVerses([]);
    setError("");
    setIsSubmitted(false);
    setLoadedContext(null);
  };

  const handleBookChange = (value) => {
    activeRequestRef.current += 1;

    const resolvedBook = resolveBookKeyFromBooks(books, value, "");

    setSelectedBook(resolvedBook);
    setSelectedChapter("");
    setVerseNumber("");
    setVerses([]);
    setError("");
    setIsSubmitted(false);
    setLoadedContext(null);

    if (resolvedBook && selectedTestamentType) {
      prefetchWholeBookInBackground(selectedTestamentType, resolvedBook);
    }
  };

  const handleChapterChange = (value) => {
    activeRequestRef.current += 1;

    setSelectedChapter(value);
    setVerseNumber("");
    setVerses([]);
    setError("");
    setIsSubmitted(false);
    setLoadedContext(null);

    if (value && selectedBook && selectedTestamentType) {
      warmChapter({
        testamentType: selectedTestamentType,
        bookKey: selectedBook,
        chapterNumber: Number(value),
      });
    }
  };

  const toggleLanguage = (lang) => {
    setSelectedLanguages((previousValue) => {
      const isSelected = previousValue.includes(lang);

      if (isSelected && previousValue.length === 1) {
        return previousValue;
      }

      if (isSelected) {
        return previousValue.filter((item) => item !== lang);
      }

      return [...previousValue, lang];
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setAlertMessage("");
    setError("");
    setVerses([]);

    if (!selectedTestament) {
      setIsSubmitted(false);
      showAlert(labels.selectTestamentAlert);
      return;
    }

    if (!selectedBook) {
      setIsSubmitted(false);
      showAlert(labels.selectBookAlert);
      return;
    }

    if (!selectedChapter) {
      setIsSubmitted(false);
      showAlert(labels.selectChapterAlert);
      return;
    }

    if (!verseNumber.trim()) {
      setIsSubmitted(false);
      showAlert(labels.enterVerseAlert);
      return;
    }

    const parsedVerseInput = parseVerseInput(verseNumber);

    if (!parsedVerseInput) {
      setIsSubmitted(false);
      showAlert(labels.invalidVerseAlert);
      return;
    }

    if (!selectedLanguages.length) {
      setIsSubmitted(false);
      showAlert(labels.selectLanguageAlert);
      return;
    }

    await loadChapter({
      testamentId: selectedTestament,
      testamentType: selectedTestamentType,
      bookKey: selectedBook,
      originalBookName: selectedOriginalBookName || selectedBook,
      chapterNumber: Number(selectedChapter),
      verseInput: verseNumber,
      shouldScroll: true,
      shouldUpdateVerseInput: true,
    });
  };

  const handleReset = () => {
    resetFormFields();
    homePrefillAppliedRef.current = false;
  };

  const currentContext = loadedContext || null;

  const chapterHeaderText = useMemo(() => {
    if (!currentContext) return "";

    const bookNames = selectedLanguages
      .map((lang) =>
        getBookNameForResultLanguage(currentContext.originalBookName, lang),
      )
      .filter(Boolean)
      .join(" | ");

    return `${bookNames} : ${currentContext.chapterNumber}`;
  }, [currentContext, selectedLanguages]);

  const previousChapterTarget = useMemo(() => {
    if (!currentContext || currentContext.chapterNumber <= 1) return null;

    return {
      ...currentContext,
      chapterNumber: currentContext.chapterNumber - 1,
    };
  }, [currentContext]);

  const nextChapterTarget = useMemo(() => {
    if (!currentContext) return null;

    const totalChapters = getBookChapterCount(
      currentContext.testamentType,
      currentContext.bookKey,
    );

    if (currentContext.chapterNumber >= totalChapters) return null;

    return {
      ...currentContext,
      chapterNumber: currentContext.chapterNumber + 1,
    };
  }, [currentContext]);

  const handleAdjacentChapterClick = (target) => {
    if (!target) return;

    loadChapter({
      testamentId: target.testamentId,
      testamentType: target.testamentType,
      bookKey: target.bookKey,
      originalBookName: target.originalBookName,
      chapterNumber: target.chapterNumber,
      verseInput: "",
      shouldScroll: true,
      shouldUpdateVerseInput: true,
    });
  };

  const getVerseNumberText = (verse) => {
    return `${verse.verse_number}. `;
  };

  const renderVerse = (verse, index) => {
    const verseNumberText = getVerseNumberText(verse);

    return (
      <div
        key={`${verse.book_name}-${verse.chapter_number}-${verse.verse_number}-${index}`}
        className="verse-row"
      >
        {selectedLanguages.map((lang, langIndex) => (
          <p
            key={`${lang}-${verse.verse_number}`}
            className={`verse-line bibleversefind-line-grid ${
              langIndex > 0 ? "bibleversefind-secondary-line" : ""
            }`}
          >
            {langIndex === 0 ? (
              <span className="verse-number-inline">{verseNumberText}</span>
            ) : (
              <span className="verse-number-spacer">{verseNumberText}</span>
            )}

            <span className="bibleversefind-verse-text">
              {verse[lang] || ""}
            </span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`bibleversefind-container ${
        isSubmitted ? "bibleversefind-result-active" : ""
      } ${isNativeInputFocused ? "bibleversefind-native-input-focused" : ""}`}
      ref={pageScrollRef}
    >
      {alertMessage ? (
        <div className="bibleversefind-alert-overlay">
          <div className="bibleversefind-alert-box">
            <div className="bibleversefind-alert-title">
              {labels.alertTitle}
            </div>

            <div className="bibleversefind-alert-message">{alertMessage}</div>

            <div className="bibleversefind-alert-actions">
              <button
                type="button"
                className="bibleversefind-alert-button"
                onClick={() => setAlertMessage("")}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="bibleversefind-form-container">
        <form onSubmit={handleSubmit}>
          <div className="bibleversefind-form-row">
            <label htmlFor="bibleversefind-testament">{labels.testament}</label>

            <CustomSelect
              id="bibleversefind-testament"
              value={selectedTestament}
              options={testamentOptions}
              placeholder={labels.select}
              onChange={handleTestamentChange}
            />
          </div>

          <div className="bibleversefind-form-row">
            <label htmlFor="bibleversefind-book">{labels.book}</label>

            <CustomSelect
              id="bibleversefind-book"
              value={selectedBook}
              options={bookOptions}
              placeholder={labels.select}
              onChange={handleBookChange}
              disabled={!selectedTestament}
            />
          </div>

          <div className="bibleversefind-form-row">
            <label htmlFor="bibleversefind-chapter">{labels.chapter}</label>

            <CustomSelect
              id="bibleversefind-chapter"
              value={selectedChapter}
              options={chapterOptions}
              placeholder={labels.select}
              onChange={handleChapterChange}
              disabled={!selectedBook}
            />
          </div>

          <div className="bibleversefind-form-row">
            <label htmlFor="bibleversefind-verse">{labels.verse}</label>

            <input
              ref={verseInputRef}
              type="text"
              id="bibleversefind-verse"
              dir="ltr"
              value={verseNumber}
              onTouchStart={rememberFindVerseInputScrollPosition}
              onMouseDown={rememberFindVerseInputScrollPosition}
              onFocus={handleNativeInputFocus}
              onBlur={handleNativeInputBlur}
              onChange={(event) => setVerseNumber(event.target.value)}
              placeholder={labels.versePlaceholder}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>

          <div className="bibleversefind-form-row bibleversefind-language-row">
            <label>{labels.language}</label>

            <div className="bibleversefind-language-checkboxes">
              {orderedLanguages.map((lang) => (
                <label key={lang}>
                  <input
                    type="checkbox"
                    value={lang}
                    checked={selectedLanguages.includes(lang)}
                    onChange={() => toggleLanguage(lang)}
                  />
                  {getLanguageText(lang)}
                </label>
              ))}
            </div>
          </div>

          <div className="bibleversefind-submit-reset-buttons">
            <button type="submit" className="bibleversefind-submit-button">
              {labels.submit}
            </button>

            <button
              type="button"
              onClick={handleReset}
              className="bibleversefind-reset-button"
            >
              {labels.reset}
            </button>
          </div>
        </form>
      </div>

      {isSubmitted ? (
        <div className="bibleversefind-result-container" ref={resultContainerRef}>
          {verses.length > 0 ? (
            <div>
              {chapterHeaderText ? (
                <div
                  className="bibleversefind-chapter-header"
                  ref={chapterHeaderRef}
                >
                  <div className="bibleversefind-header-left">
                    <div className="bibleversefind-chapter-header-title">
                      {chapterHeaderText}
                    </div>
                  </div>

                  <div className="bibleversefind-chapter-nav">
                    <button
                      type="button"
                      className="bibleversefind-chapter-nav-button"
                      disabled={!previousChapterTarget}
                      onClick={() =>
                        handleAdjacentChapterClick(previousChapterTarget)
                      }
                    >
                      {labels.previous}
                    </button>

                    <button
                      type="button"
                      className="bibleversefind-chapter-nav-button"
                      disabled={!nextChapterTarget}
                      onClick={() =>
                        handleAdjacentChapterClick(nextChapterTarget)
                      }
                    >
                      {labels.next}
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="bibleversefind-results-list">
                {verses.map((verse, index) => renderVerse(verse, index))}
              </div>
            </div>
          ) : error ? (
            <p>{error}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default FindVerse;